import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiUploadCloud, FiSearch, FiCheck, FiLink, FiImage, FiChevronLeft, FiChevronRight, FiAlertCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Modal, Button, Spinner } from '../../../components/common';
import { mediaService, getApiError } from '../../../services/cmsService';
import { formatFileSize } from '../../../utils/fileUtils';
import { useDebounce } from '../../../hooks';
import './MediaPicker.css';

const PER_PAGE = 24;
const ACCEPTED = 'image/jpeg,image/jpg,image/png,image/webp,image/svg+xml';
const MAX_SIZE_MB = 5;

const TABS = [
  { key: 'library', label: 'Library', icon: FiImage },
  { key: 'upload', label: 'Upload', icon: FiUploadCloud },
  { key: 'url', label: 'From URL', icon: FiLink },
];

/**
 * Modal for choosing an image URL: browse the media library, upload a new
 * file, or paste an external URL. Always resolves to a plain URL string,
 * which is what every CMS image column stores.
 */
const MediaPicker = ({ isOpen, onClose, onSelect, folder = 'cms', title = 'Select Image' }) => {
  // `folder` is the upload destination only — the library lists everything so
  // an image uploaded elsewhere can still be reused here.
  const [tab, setTab] = useState('library');
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [urlValue, setUrlValue] = useState('');
  const [loadError, setLoadError] = useState('');

  const fileInputRef = useRef(null);
  const debouncedSearch = useDebounce(search, 350);

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await mediaService.getAll({ page, limit: PER_PAGE, search: debouncedSearch });
      setMedia(response.data.media);
      setTotalPages(response.data.totalPages);
      setTotal(response.data.total);
    } catch (error) {
      setMedia([]);
      setLoadError(getApiError(error, 'Could not load the media library'));
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch]);

  useEffect(() => {
    if (isOpen && tab === 'library') fetchMedia();
  }, [isOpen, tab, fetchMedia]);

  // Reset transient state each time the picker opens
  useEffect(() => {
    if (isOpen) {
      setTab('library');
      setSelected(null);
      setUrlValue('');
      setSearch('');
      setPage(1);
    }
  }, [isOpen]);

  const uploadFiles = async (files) => {
    const list = Array.from(files);
    if (!list.length) return;

    const file = list[0];
    if (!ACCEPTED.split(',').includes(file.type)) {
      toast.error('Only JPG, PNG, WEBP and SVG files are allowed');
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`"${file.name}" is larger than ${MAX_SIZE_MB}MB`);
      return;
    }

    setUploading(true);
    setProgress(0);
    try {
      const response = await mediaService.upload(file, { folder, onProgress: setProgress });
      toast.success('Image uploaded');
      onSelect(response.data.url, response.data);
      onClose();
    } catch (error) {
      toast.error(getApiError(error, 'Upload failed'));
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const handleConfirm = () => {
    if (tab === 'url') {
      const trimmed = urlValue.trim();
      if (!trimmed) {
        toast.error('Paste an image URL first');
        return;
      }
      onSelect(trimmed, null);
      onClose();
      return;
    }
    if (!selected) {
      toast.error('Select an image first');
      return;
    }
    onSelect(selected.url, selected);
    onClose();
  };

  const renderLibrary = () => (
    <>
      <div className="mp-toolbar">
        <div className="mp-search">
          <FiSearch />
          <input
            type="text"
            value={search}
            placeholder="Search by file name…"
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <span className="mp-count">{total} {total === 1 ? 'file' : 'files'}</span>
      </div>

      {loading ? (
        <div className="mp-state"><Spinner size="medium" /></div>
      ) : loadError ? (
        <div className="mp-state mp-state--error">
          <FiAlertCircle />
          <p>{loadError}</p>
          <Button variant="outline" size="small" onClick={fetchMedia}>Retry</Button>
        </div>
      ) : media.length === 0 ? (
        <div className="mp-state">
          <FiImage />
          <p>{debouncedSearch ? 'No images match that search.' : 'The library is empty.'}</p>
          <Button variant="outline" size="small" icon={<FiUploadCloud />} onClick={() => setTab('upload')}>
            Upload an image
          </Button>
        </div>
      ) : (
        <div className="mp-grid">
          {media.map((item) => (
            <button
              type="button"
              key={item.id}
              className={`mp-tile ${selected?.id === item.id ? 'selected' : ''}`}
              onClick={() => setSelected(item)}
              onDoubleClick={() => {
                onSelect(item.url, item);
                onClose();
              }}
              title={item.originalName}
            >
              <span className="mp-thumb">
                <img src={item.url} alt={item.altText || item.originalName} loading="lazy" />
              </span>
              <span className="mp-tile-name">{item.originalName || 'Untitled'}</span>
              <span className="mp-tile-meta">{formatFileSize(item.size)}</span>
              <span className="mp-check"><FiCheck /></span>
            </button>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mp-pager">
          <Button variant="ghost" size="small" icon={<FiChevronLeft />} disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            Prev
          </Button>
          <span>Page {page} of {totalPages}</span>
          <Button variant="ghost" size="small" icon={<FiChevronRight />} iconPosition="right" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      )}
    </>
  );

  const renderUpload = () => (
    <div
      className={`mp-dropzone ${dragActive ? 'active' : ''} ${uploading ? 'busy' : ''}`}
      onClick={() => !uploading && fileInputRef.current?.click()}
      onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
      onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED}
        hidden
        onChange={(e) => e.target.files?.length && uploadFiles(e.target.files)}
      />
      {uploading ? (
        <>
          <Spinner size="medium" />
          <p className="mp-dz-title">Uploading… {progress}%</p>
          <div className="mp-progress"><span style={{ width: `${progress}%` }} /></div>
        </>
      ) : (
        <>
          <span className="mp-dz-icon"><FiUploadCloud /></span>
          <p className="mp-dz-title">Click to browse or drop an image here</p>
          <p className="mp-dz-hint">JPG, PNG, WEBP or SVG · up to {MAX_SIZE_MB}MB · saved to the “{folder}” folder</p>
        </>
      )}
    </div>
  );

  const renderUrl = () => (
    <div className="mp-url">
      <label className="mp-url-label" htmlFor="mp-url-input">Image URL</label>
      <input
        id="mp-url-input"
        type="url"
        className="mp-url-input"
        placeholder="https://backend.designinfra.co.in/storage/cms/…"
        value={urlValue}
        onChange={(e) => setUrlValue(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
      />
      <p className="mp-url-hint">Use this for images already hosted elsewhere. The URL is stored as-is.</p>
      {urlValue.trim() && (
        <div className="mp-url-preview">
          <img src={urlValue.trim()} alt="Preview" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        </div>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="xlarge"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          {tab !== 'upload' && (
            <Button onClick={handleConfirm} disabled={tab === 'library' ? !selected : !urlValue.trim()}>
              Use this image
            </Button>
          )}
        </>
      }
    >
      <div className="media-picker">
        <div className="mp-tabs" role="tablist">
          {TABS.map((item) => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={tab === item.key}
              className={`mp-tab ${tab === item.key ? 'active' : ''}`}
              onClick={() => setTab(item.key)}
            >
              <item.icon />
              <span>{item.label}</span>
            </button>
          ))}
        </div>

        <div className="mp-body">
          {tab === 'library' && renderLibrary()}
          {tab === 'upload' && renderUpload()}
          {tab === 'url' && renderUrl()}
        </div>
      </div>
    </Modal>
  );
};

export default MediaPicker;
